import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Building2, Search, Trash2, Pencil, CreditCard, Users, Eye, Package } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { ar } from "date-fns/locale";
import NurseryCreateDialog from "@/components/admin/nurseries/NurseryCreateDialog";
import NurseryEditDialog from "@/components/admin/nurseries/NurseryEditDialog";
import NurserySubscriptionDialog, { type SubscriptionFormData } from "@/components/admin/nurseries/NurserySubscriptionDialog";
import NurseryModulesDialog from "@/components/admin/nurseries/NurseryModulesDialog";

const planLabels: Record<string, string> = { basic: "أساسي", standard: "قياسي", premium: "مميز" };
const statusLabels: Record<string, string> = { active: "نشط", suspended: "موقوف", expired: "منتهي", cancelled: "ملغي" };
const statusVariants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  active: "default", suspended: "outline", expired: "secondary", cancelled: "destructive",
};

const AdminNurseries = () => {
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [subDialogOpen, setSubDialogOpen] = useState(false);
  const [editingNursery, setEditingNursery] = useState<{ id: string; name: string; address: string; phone: string } | null>(null);
  const [subForm, setSubForm] = useState<SubscriptionFormData | null>(null);
  const [modulesDialogOpen, setModulesDialogOpen] = useState(false);
  const [modulesNursery, setModulesNursery] = useState<{ id: string; name: string; plan?: string } | null>(null);
  const [form, setForm] = useState({
    name: "", address: "", phone: "", ownerEmail: "",
    plan: "basic", amount: "1000", billingCycle: "monthly",
  });
  const queryClient = useQueryClient();

  const { data: nurseries, isLoading } = useQuery({
    queryKey: ["admin-nurseries"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("nurseries")
        .select("*, nursery_subscriptions(*)")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  // Stats
  const totalNurseries = nurseries?.length || 0;
  const activeCount = nurseries?.filter(n => n.nursery_subscriptions?.[0]?.status === "active").length || 0;
  const trialCount = nurseries?.filter(n => n.nursery_subscriptions?.[0]?.is_trial).length || 0;

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("nurseries").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-nurseries"] });
      queryClient.invalidateQueries({ queryKey: ["admin-stats"] });
      toast.success("تم حذف الحضانة");
    },
    onError: () => toast.error("فشل حذف الحضانة"),
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke("create-nursery", {
        body: {
          name: form.name, address: form.address, phone: form.phone,
          ownerEmail: form.ownerEmail, plan: form.plan, amount: form.amount, billingCycle: form.billingCycle,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-nurseries"] });
      queryClient.invalidateQueries({ queryKey: ["admin-stats"] });
      toast.success("تم إنشاء الحضانة بنجاح");
      setDialogOpen(false);
      setForm({ name: "", address: "", phone: "", ownerEmail: "", plan: "basic", amount: "1000", billingCycle: "monthly" });
    },
    onError: (err: any) => toast.error(err.message || "فشل إنشاء الحضانة"),
  });

  const editMutation = useMutation({
    mutationFn: async () => {
      if (!editingNursery) return;
      const { error } = await supabase
        .from("nurseries")
        .update({ name: editingNursery.name, address: editingNursery.address || null, phone: editingNursery.phone || null })
        .eq("id", editingNursery.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-nurseries"] });
      toast.success("تم تحديث بيانات الحضانة");
      setEditDialogOpen(false);
      setEditingNursery(null);
    },
    onError: () => toast.error("فشل تحديث البيانات"),
  });

  const subMutation = useMutation({
    mutationFn: async () => {
      if (!subForm) return;
      const payload = {
        plan: subForm.plan,
        status: subForm.status,
        amount: parseFloat(subForm.amount) || 0,
        billing_cycle: subForm.billing_cycle,
        started_at: subForm.started_at ? new Date(subForm.started_at).toISOString() : new Date().toISOString(),
        expires_at: subForm.expires_at ? new Date(subForm.expires_at).toISOString() : null,
        is_trial: subForm.is_trial,
        trial_ends_at: subForm.is_trial && subForm.trial_ends_at ? new Date(subForm.trial_ends_at).toISOString() : null,
        payment_method: subForm.payment_method || null,
        notes: subForm.notes || null,
        discount_percent: parseFloat(subForm.discount_percent) || 0,
        discount_type: subForm.discount_type === "none" ? null : subForm.discount_type,
        pricing_model: subForm.pricing_model || "fixed",
        price_per_child: subForm.pricing_model === "per_child" ? parseFloat(subForm.price_per_child) || null : null,
        min_charge: subForm.pricing_model === "per_child" ? parseFloat(subForm.min_charge) || null : null,
      };

      if (subForm.id) {
        const { error } = await supabase.from("nursery_subscriptions").update(payload).eq("id", subForm.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("nursery_subscriptions").insert({ ...payload, nursery_id: subForm.nursery_id });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-nurseries"] });
      toast.success("تم تحديث الاشتراك بنجاح");
      setSubDialogOpen(false);
      setSubForm(null);
    },
    onError: () => toast.error("فشل تحديث الاشتراك"),
  });

  const openSubscriptionEdit = (nursery: any) => {
    const sub = nursery.nursery_subscriptions?.[0];
    setSubForm({
      id: sub?.id || "",
      nursery_id: nursery.id,
      nursery_name: nursery.name,
      plan: sub?.plan || "free",
      status: sub?.status || "active",
      amount: String(sub?.amount || 0),
      billing_cycle: sub?.billing_cycle || "monthly",
      started_at: sub?.started_at ? format(new Date(sub.started_at), "yyyy-MM-dd") : format(new Date(), "yyyy-MM-dd"),
      expires_at: sub?.expires_at ? format(new Date(sub.expires_at), "yyyy-MM-dd") : "",
      is_trial: sub?.is_trial || false,
      trial_ends_at: sub?.trial_ends_at ? format(new Date(sub.trial_ends_at), "yyyy-MM-dd") : "",
      payment_method: sub?.payment_method || "",
      notes: sub?.notes || "",
      discount_percent: String(sub?.discount_percent || 0),
      discount_type: sub?.discount_type || "none",
      pricing_model: sub?.pricing_model || "fixed",
      price_per_child: String(sub?.price_per_child || ""),
      min_charge: String(sub?.min_charge || ""),
    });
    setSubDialogOpen(true);
  };

  const filtered = nurseries?.filter((n) =>
    n.name.toLowerCase().includes(search.toLowerCase()) ||
    n.address?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">إدارة الحضانات</h1>
          <p className="text-muted-foreground text-sm">عرض وإدارة جميع الحضانات المسجلة في المنصة</p>
        </div>
        <NurseryCreateDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          form={form}
          onFormChange={setForm}
          onCreate={() => createMutation.mutate()}
          isPending={createMutation.isPending}
        />
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4 flex items-center gap-3">
            <Building2 className="w-8 h-8 text-primary" />
            <div>
              <p className="text-2xl font-bold">{totalNurseries}</p>
              <p className="text-xs text-muted-foreground">إجمالي الحضانات</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 flex items-center gap-3">
            <CreditCard className="w-8 h-8 text-green-600" />
            <div>
              <p className="text-2xl font-bold">{activeCount}</p>
              <p className="text-xs text-muted-foreground">اشتراكات نشطة</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 flex items-center gap-3">
            <Eye className="w-8 h-8 text-amber-500" />
            <div>
              <p className="text-2xl font-bold">{trialCount}</p>
              <p className="text-xs text-muted-foreground">فترات تجريبية</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <Building2 className="w-5 h-5" />
            <CardTitle>الحضانات ({filtered?.length || 0})</CardTitle>
            <div className="flex-1" />
            <div className="relative w-64">
              <Search className="absolute right-3 top-2.5 w-4 h-4 text-muted-foreground" />
              <Input placeholder="بحث..." value={search} onChange={(e) => setSearch(e.target.value)} className="pr-9" />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-center py-8 text-muted-foreground">جاري التحميل...</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>الاسم</TableHead>
                  <TableHead>العنوان</TableHead>
                  <TableHead>الهاتف</TableHead>
                  <TableHead>الاشتراك</TableHead>
                  <TableHead>الحالة</TableHead>
                  <TableHead>المبلغ</TableHead>
                  <TableHead>تاريخ الإنشاء</TableHead>
                  <TableHead>إجراءات</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered?.map((nursery) => {
                  const sub = nursery.nursery_subscriptions?.[0];
                  return (
                    <TableRow key={nursery.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {nursery.logo_url && (
                            <img src={nursery.logo_url} className="w-8 h-8 rounded-full object-cover" />
                          )}
                          <div>
                            <p>{nursery.name}</p>
                            {sub?.is_trial && <Badge variant="outline" className="text-[10px] px-1">تجريبي</Badge>}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{nursery.address || "—"}</TableCell>
                      <TableCell dir="ltr">{nursery.phone || "—"}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">{planLabels[sub?.plan || "free"] || sub?.plan}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={statusVariants[sub?.status || "active"] || "secondary"}>
                          {statusLabels[sub?.status || "active"] || sub?.status}
                        </Badge>
                      </TableCell>
                      <TableCell dir="ltr" className="font-mono text-sm">
                        {sub?.amount ? `${sub.amount} ج.م` : "—"}
                      </TableCell>
                      <TableCell>
                        {format(new Date(nursery.created_at), "dd MMM yyyy", { locale: ar })}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="icon" variant="ghost" title="تعديل البيانات" onClick={() => {
                            setEditingNursery({ id: nursery.id, name: nursery.name, address: nursery.address || "", phone: nursery.phone || "" });
                            setEditDialogOpen(true);
                          }}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button size="icon" variant="ghost" title="تعديل الاشتراك" onClick={() => openSubscriptionEdit(nursery)}>
                            <CreditCard className="w-4 h-4" />
                          </Button>
                          <Button size="icon" variant="ghost" title="إدارة الموديولات" onClick={() => {
                            const sub = nursery.nursery_subscriptions?.[0];
                            setModulesNursery({ id: nursery.id, name: nursery.name, plan: sub?.plan || "basic" });
                            setModulesDialogOpen(true);
                          }}>
                            <Package className="w-4 h-4" />
                          </Button>
                          <Button size="icon" variant="ghost" className="text-destructive" title="حذف" onClick={() => {
                            if (confirm("هل أنت متأكد من حذف هذه الحضانة؟ سيتم حذف جميع بياناتها!")) {
                              deleteMutation.mutate(nursery.id);
                            }
                          }}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!filtered?.length && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      لا توجد حضانات
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <NurseryEditDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        nursery={editingNursery}
        onNurseryChange={setEditingNursery}
        onSave={() => editMutation.mutate()}
        isPending={editMutation.isPending}
      />

      <NurserySubscriptionDialog
        open={subDialogOpen}
        onOpenChange={setSubDialogOpen}
        form={subForm}
        onFormChange={setSubForm}
        onSave={() => subMutation.mutate()}
        isPending={subMutation.isPending}
      />
      <NurseryModulesDialog
        open={modulesDialogOpen}
        onOpenChange={setModulesDialogOpen}
        nursery={modulesNursery}
      />
    </div>
  );
};

export default AdminNurseries;
